import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import HomePostsSection from '../../../organisms/HomeEditorial/HomePostsSection';
import BandSectionActionButton from '../../../molecules/EditorialSectionActionButton';
import { BAND_POST_VISIBILITY_OPTIONS } from '../../../molecules/PostVisibilityControl/options';

/**
 * Announcements section. Band posts use the same post cards,
 * composer modal, engagement controls, and delete confirmation as Home/Feed.
 */
export default function BandPostsSection({
  id = 'posts',
  eyebrow,
  title,
  side,
  posts = [],
  currentUserId,
  readOnly = false,
  onStartPost,
  composeLabel,
  emptyTitle,
  emptyDescription,
  postComposerProps = {},
  publicMedia = false,
}) {
  const { t } = useTranslation();
  const composerLabel = composeLabel || t('bandPage.posts.startPost', { defaultValue: 'Create post' });
  const canCompose = typeof onStartPost === 'function';

  const renderComposeButton = useCallback(({ label, onClick }) => (
    <BandSectionActionButton
      label={label}
      onClick={onClick}
      variant="accent"
    />
  ), []);

  const createAction = canCompose ? (
    <BandSectionActionButton
      label={composerLabel}
      onClick={onStartPost}
      variant="accent"
    />
  ) : null;

  return (
    <HomePostsSection
      id={id}
      eyebrow={eyebrow || t('bandPage.posts.eyebrow')}
      title={title || t('bandPage.posts.title')}
      side={side || createAction}
      posts={posts}
      currentUserId={currentUserId}
      readOnly={readOnly}
      allowComposer={canCompose}
      openCreateComposer={onStartPost}
      showComposerToolbar={false}
      renderComposeButton={renderComposeButton}
      composeLabel={composerLabel}
      postVisibilityOptions={BAND_POST_VISIBILITY_OPTIONS}
      publicMedia={publicMedia}
      emptyTitle={emptyTitle || t('bandPage.posts.emptyTitle', { defaultValue: 'No announcements yet' })}
      emptyDescription={emptyDescription || t('bandPage.posts.emptyDescription', { defaultValue: 'When the band shares an update, it will appear here.' })}
      loadingTitle={t('bandPage.posts.loadingTitle', { defaultValue: 'Loading announcements...' })}
      postsErrorTitle={t('bandPage.posts.loadErrorTitle', { defaultValue: 'Unable to load announcements' })}
      postsErrorDescription={t('bandPage.posts.loadErrorDescription', { defaultValue: 'Band posts could not be loaded right now.' })}
      retryPostsLabel={t('bandPage.posts.retry', { defaultValue: 'Try again' })}
      {...postComposerProps}
    />
  );
}

BandPostsSection.propTypes = {
  id: PropTypes.string,
  eyebrow: PropTypes.node,
  title: PropTypes.node,
  side: PropTypes.node,
  posts: PropTypes.array,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  readOnly: PropTypes.bool,
  onStartPost: PropTypes.func,
  composeLabel: PropTypes.node,
  emptyTitle: PropTypes.node,
  emptyDescription: PropTypes.node,
  postComposerProps: PropTypes.object,
  publicMedia: PropTypes.bool,
};
